import React, { useState, useRef } from 'react';
import { RotateCcw, ChevronLeft, ChevronRight, Info, X, GripVertical, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface ModelRun {
  id: string;
  modelId: string;
  modelName: string;
  timestamp: Date;
  runTime: string;
  status: 'available' | 'unavailable' | 'processing';
  selected: boolean;
}
interface Model {
  id: string;
  name: string;
  color: string;
  badge?: string;
}
const models: Model[] = [{
  id: 'ept2',
  name: 'EPT-2',
  color: '#ff006b'
}, {
  id: 'ept2early',
  name: 'EPT-2 Early',
  color: '#d946ef',
  badge: 'Early'
}, {
  id: 'ecifs',
  name: 'EC-IFS',
  color: '#06b6d4'
}, {
  id: 'gfs',
  name: 'GFS',
  color: '#f59e0b'
}, {
  id: 'ept2e',
  name: 'EPT-2e',
  color: '#a855f7'
}];
const timeSlots = ['00', '06', '12', '18'];
export function ModelsPanel() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [modelRuns, setModelRuns] = useState<ModelRun[]>(generateInitialRuns());
  const [showTooltip, setShowTooltip] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  function generateInitialRuns(): ModelRun[] {
    const runs: ModelRun[] = [];
    const baseDate = new Date();
    const now = new Date();
    models.forEach(model => {
      // Generate 7 days of data for scrolling
      for (let dayOffset = -2; dayOffset < 5; dayOffset++) {
        timeSlots.forEach((time, idx) => {
          const timestamp = new Date(baseDate);
          timestamp.setDate(timestamp.getDate() + dayOffset);
          timestamp.setHours(parseInt(time), 0, 0, 0);
          const isPast = timestamp < now;
          runs.push({
            id: `${model.id}-day${dayOffset}-${time}`,
            modelId: model.id,
            modelName: model.name,
            timestamp,
            runTime: `${timestamp.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            })}, ${time}:00`,
            status: isPast ? Math.random() > 0.3 ? 'available' : 'unavailable' : idx === 3 && model.id === 'ept2e' && dayOffset === 0 ? 'processing' : Math.random() > 0.2 ? 'available' : 'unavailable',
            selected: model.id === 'ept2' && time === '18' && dayOffset === 0 || model.id === 'ept2early' && time === '12' && dayOffset === 0
          });
        });
      }
    });
    return runs;
  }
  const selectedRuns = modelRuns.filter(r => r.selected);
  const toggleRun = (runId: string) => {
    setModelRuns(runs => runs.map(r => r.id === runId ? {
      ...r,
      selected: !r.selected
    } : r));
  };
  const selectLatestForModel = (modelId: string) => {
    // Only get available PAST runs (not future)
    const now = new Date();
    const modelRunsList = modelRuns.filter(r => r.modelId === modelId && r.status === 'available' && r.timestamp < now);
    if (modelRunsList.length === 0) return;
    const latest = modelRunsList.reduce((prev, current) => current.timestamp > prev.timestamp ? current : prev);
    setModelRuns(runs => runs.map(r => r.modelId === modelId ? {
      ...r,
      selected: r.id === latest.id
    } : r));
    // Scroll to make the specific selected time slot visible
    setTimeout(() => {
      if (scrollContainerRef.current) {
        const displayDays = getDisplayDays();
        const latestDayIndex = displayDays.findIndex(day => day.getDate() === latest.timestamp.getDate() && day.getMonth() === latest.timestamp.getMonth() && day.getFullYear() === latest.timestamp.getFullYear());
        if (latestDayIndex !== -1) {
          // Calculate which time slot (0-3) within the day
          const latestHour = latest.timestamp.getHours();
          const timeSlotIndex = timeSlots.findIndex(slot => parseInt(slot) === latestHour);
          // Each day is 176px wide + 24px gap (gap-6)
          const dayWidth = 176;
          const gapWidth = 24;
          const columnWidth = (dayWidth + gapWidth) / 4; // Width per time slot including proportional gap
          // Calculate position: sum of all previous days + position within current day
          const dayStartPosition = latestDayIndex * (dayWidth + gapWidth);
          const columnOffset = timeSlotIndex * columnWidth;
          const targetPosition = dayStartPosition + columnOffset;
          // Center the column in view
          const containerWidth = scrollContainerRef.current.clientWidth;
          const scrollPosition = targetPosition - containerWidth / 2 + columnWidth / 2;
          scrollContainerRef.current.scrollTo({
            left: Math.max(0, scrollPosition),
            behavior: 'smooth'
          });
        }
      }
    }, 50);
  };
  const clearModelSelections = (modelId: string) => {
    setModelRuns(runs => runs.map(r => r.modelId === modelId ? {
      ...r,
      selected: false
    } : r));
  };
  const resetAllSelections = () => {
    setModelRuns(runs => runs.map(r => ({
      ...r,
      selected: false
    })));
  };
  const removeRun = (runId: string) => {
    toggleRun(runId);
  };
  const scrollToPreviousDay = () => {
    if (scrollContainerRef.current) {
      const dayWidth = scrollContainerRef.current.scrollWidth / 7; // 7 days total
      scrollContainerRef.current.scrollBy({
        left: -dayWidth,
        behavior: 'smooth'
      });
    }
  };
  const scrollToNextDay = () => {
    if (scrollContainerRef.current) {
      const dayWidth = scrollContainerRef.current.scrollWidth / 7; // 7 days total
      scrollContainerRef.current.scrollBy({
        left: dayWidth,
        behavior: 'smooth'
      });
    }
  };
  const scrollToToday = () => {
    if (scrollContainerRef.current) {
      const displayDays = getDisplayDays();
      const today = new Date();
      const todayIndex = displayDays.findIndex(day => day.toDateString() === today.toDateString());
      if (todayIndex !== -1) {
        const dayWidth = scrollContainerRef.current.scrollWidth / 7;
        const scrollPosition = todayIndex * dayWidth;
        scrollContainerRef.current.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  };
  const getDisplayDays = () => {
    const days = [];
    const baseDate = new Date();
    for (let i = -2; i < 5; i++) {
      const date = new Date(baseDate);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };
  const displayDays = getDisplayDays();
  const monthYear = new Date().toLocaleDateString('en-US', {
    month: 'long'
  });
  const now = new Date();
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 flex flex-col h-full shadow-sm shadow-slate-200/50 relative overflow-hidden" data-id="element-412">
      {/* Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-413">
        <div className="flex items-center gap-2" data-id="element-414">
          <h3 className="text-lg font-semibold text-slate-900" data-id="element-415">Models</h3>
          <div className="relative" data-id="element-416">
            <button onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className="p-1 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-417">
              <Info size={14} data-id="element-418" />
            </button>
            <AnimatePresence data-id="element-419">
              {showTooltip && <motion.div initial={{
              opacity: 0,
              y: -5
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -5
            }} className="absolute left-0 top-6 w-64 bg-slate-900 text-white text-xs p-3 rounded-lg shadow-xl z-30" data-id="element-420">
                  Choose which forecast model runs are included in the analysis.
                  Only selected runs are loaded and shown below.
                  <div className="absolute -top-1 left-2 w-2 h-2 bg-slate-900 rotate-45" data-id="element-421" />
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-422">
          <button onClick={resetAllSelections} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-100/80 hover:bg-slate-200/80 text-xs font-medium text-slate-700 transition-colors" data-id="element-423">
            <RotateCcw size={12} data-id="element-424" />
            Reset
          </button>
          <div className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 transition-colors p-1" data-id="element-425">
            <GripVertical size={16} data-id="element-426" />
          </div>
        </div>
      </div>

      {/* Included in Analysis Summary */}
      <div className="mb-4 pb-4 border-b border-slate-200/50" data-id="element-427">
        <div className="text-xs font-medium text-slate-700 mb-2" data-id="element-428">
          Included in analysis
        </div>
        <div className="flex flex-wrap gap-1.5" data-id="element-429">
          <AnimatePresence mode="popLayout" data-id="element-430">
            {selectedRuns.length === 0 ? <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="text-xs text-slate-400 italic" data-id="element-431">
                No runs selected
              </motion.div> : selectedRuns.map(run => <motion.div key={run.id} initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} className="flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200/50 text-xs" data-id="element-432">
                  <span className="font-medium text-slate-900" data-id="element-433">
                    {run.modelName}
                  </span>
                  <span className="text-slate-500" data-id="element-434">·</span>
                  <span className="text-slate-600 font-mono text-[10px]" data-id="element-435">
                    {run.runTime}
                  </span>
                  <button onClick={() => removeRun(run.id)} className="p-0.5 hover:bg-pink-200/50 rounded transition-colors" data-id="element-436">
                    <X size={10} className="text-slate-500" data-id="element-437" />
                  </button>
                </motion.div>)}
          </AnimatePresence>
        </div>
      </div>

      {/* Date Navigator */}
      <div className="mb-4 pb-4 border-b border-slate-200/50" data-id="element-438">
        <div className="flex items-center justify-between mb-3" data-id="element-439">
          <div className="text-xs font-medium text-slate-500 w-[120px] flex-shrink-0" data-id="element-440">
            Time (UTC)
          </div>
          <div className="text-sm font-medium text-slate-500" data-id="element-441">{monthYear}</div>
          <div className="w-[90px] flex-shrink-0 flex justify-end" data-id="element-442">
            <button onClick={scrollToToday} className="text-xs font-medium text-pink-600 hover:text-pink-700 transition-colors" data-id="element-443">
              Today
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Timeline Container */}
      <div className="flex-1 overflow-y-auto custom-scrollbar" data-id="element-444">
        <div className="flex gap-4" data-id="element-445">
          {/* Model Names Column (Fixed) */}
          <div className="w-[120px] flex-shrink-0" data-id="element-446">
            {/* Header with navigation */}
            <div className="h-[60px] mb-3 flex items-center justify-center" data-id="element-447">
              <button onClick={scrollToPreviousDay} className="p-1 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors text-slate-600" data-id="element-448">
                <ChevronLeft size={18} data-id="element-449" />
              </button>
            </div>

            {/* Model names */}
            <div className="space-y-3" data-id="element-450">
              {models.map(model => <div key={model.id} className="flex items-center gap-2 h-[44px] border-b border-slate-100/50 last:border-0" data-id="element-451">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                backgroundColor: model.color
              }} data-id="element-452" />
                  <div className="flex flex-col min-w-0" data-id="element-453">
                    <div className="flex items-center gap-1" data-id="element-454">
                      <span className="text-xs font-bold text-slate-900 truncate" data-id="element-455">
                        {model.name}
                      </span>
                      {model.badge && <span className="text-[9px] px-1 py-0.5 rounded bg-purple-100 text-purple-700 font-medium flex-shrink-0" data-id="element-456">
                          {model.badge}
                        </span>}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Scrollable Timeline */}
          <div ref={scrollContainerRef} className="flex-1 overflow-x-auto custom-scrollbar" data-id="element-457">
            <div className="min-w-max" data-id="element-458">
              {/* Date & Time Headers */}
              <div className="flex gap-6 h-[60px] mb-3" data-id="element-459">
                {displayDays.map((day, dayIndex) => <div key={dayIndex} className="text-center relative" style={{
                minWidth: '176px'
              }} data-id="element-460">
                    {/* Vertical divider (except for last day) */}
                    {dayIndex < displayDays.length - 1 && <div className="absolute right-[-12px] top-0 bottom-0 w-px bg-slate-200/50" data-id="element-461" />}
                    <div className="text-lg font-bold text-slate-900 mb-2" data-id="element-462">
                      {day.getDate()}
                    </div>
                    <div className="grid grid-cols-4 gap-2" data-id="element-463">
                      {timeSlots.map(time => <div key={time} className="text-xs text-slate-500 font-mono text-center w-10" data-id="element-464">
                          {time}:00
                        </div>)}
                    </div>
                  </div>)}
              </div>

              {/* Model Checkboxes Grid */}
              <div className="space-y-3" data-id="element-465">
                {models.map(model => {
                const modelRunsList = modelRuns.filter(r => r.modelId === model.id);
                return <div key={model.id} className="flex gap-6 h-[44px] border-b border-slate-100/50 last:border-0" data-id="element-466">
                      {displayDays.map((day, dayIndex) => <div key={dayIndex} className="grid grid-cols-4 gap-2 items-center relative" style={{
                    minWidth: '176px'
                  }} data-id="element-467">
                          {/* Vertical divider (except for last day) */}
                          {dayIndex < displayDays.length - 1 && <div className="absolute right-[-12px] top-0 bottom-0 w-px bg-slate-200/50" data-id="element-468" />}
                          {timeSlots.map(time => {
                      const run = modelRunsList.find(r => {
                        const runDay = r.timestamp.getDate();
                        const runMonth = r.timestamp.getMonth();
                        const runYear = r.timestamp.getFullYear();
                        const runTime = r.timestamp.getHours().toString().padStart(2, '0');
                        return runDay === day.getDate() && runMonth === day.getMonth() && runYear === day.getFullYear() && runTime === time;
                      });
                      // Create slot timestamp for checking if past/future
                      const slotTimestamp = new Date(day);
                      slotTimestamp.setHours(parseInt(time), 0, 0, 0);
                      const isPast = slotTimestamp < now;
                      const isFuture = slotTimestamp > now;
                      if (!run) {
                        // No run exists for this slot - show disabled if past
                        if (isPast) {
                          return <div key={time} className="flex justify-center w-10" data-id="element-469">
                                    <div className="w-5 h-5 flex-shrink-0 rounded border-2 bg-slate-100 border-slate-200 opacity-40 cursor-not-allowed" title="Run not available for this time" data-id="element-470" />
                                  </div>;
                        }
                        return <div key={time} className="w-10" data-id="element-471" />;
                      }
                      const isAvailable = run.status === 'available';
                      const isProcessing = run.status === 'processing';
                      const isSelected = run.selected;
                      // Show disabled checkbox for:
                      // 1. Unavailable runs (past or future)
                      // 2. Future runs (not yet available)
                      if (!isAvailable || isFuture) {
                        return <div key={run.id} className="flex justify-center w-10" data-id="element-472">
                                  <div className="w-5 h-5 flex-shrink-0 rounded border-2 bg-slate-100 border-slate-200 opacity-40 cursor-not-allowed" title={isFuture ? 'Run not yet available' : 'Run not available for this time'} data-id="element-473" />
                                </div>;
                      }
                      // Show active checkbox for available past runs only
                      return <div key={run.id} className="flex justify-center w-10" data-id="element-474">
                                <button onClick={() => toggleRun(run.id)} className={`w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-pink-500 border-pink-500 shadow-sm' : 'bg-white border-slate-300 hover:border-pink-400 hover:bg-pink-50'}`} title={isSelected ? 'Click to exclude' : 'Click to include'} data-id="element-475">
                                  {isSelected && <Check size={12} className="text-white" strokeWidth={3} data-id="element-476" />}
                                  {isProcessing && <motion.div className="w-2 h-2 rounded-full bg-amber-500" animate={{
                            scale: [1, 1.3, 1],
                            opacity: [1, 0.5, 1]
                          }} transition={{
                            repeat: Infinity,
                            duration: 2
                          }} data-id="element-477" />}
                                </button>
                              </div>;
                    })}
                        </div>)}
                    </div>;
              })}
              </div>
            </div>
          </div>

          {/* Action Buttons Column (Fixed) */}
          <div className="w-[90px] flex-shrink-0" data-id="element-478">
            {/* Header with navigation */}
            <div className="h-[60px] mb-3 flex items-center justify-center" data-id="element-479">
              <button onClick={scrollToNextDay} className="p-1 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors text-slate-600" data-id="element-480">
                <ChevronRight size={18} data-id="element-481" />
              </button>
            </div>

            {/* Action buttons */}
            <div className="space-y-3" data-id="element-482">
              {models.map(model => {
              const modelRunsList = modelRuns.filter(r => r.modelId === model.id);
              const hasSelections = modelRunsList.some(r => r.selected);
              return <div key={model.id} className="flex items-center justify-end gap-1 h-[44px] border-b border-slate-100/50 last:border-0" data-id="element-483">
                    <button onClick={() => selectLatestForModel(model.id)} className="text-[10px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition-colors" data-id="element-484">
                      Latest
                    </button>
                    {hasSelections && <button onClick={() => clearModelSelections(model.id)} className="text-[10px] px-2 py-1 rounded bg-pink-50 hover:bg-pink-100 text-pink-700 font-medium transition-colors" data-id="element-485">
                        Clear
                      </button>}
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-slate-200/50 flex justify-between items-center text-xs text-slate-600" data-id="element-486">
        <div data-id="element-487">
          <span className="text-[#ff006b] font-medium" data-id="element-488">
            {selectedRuns.length}
          </span>{' '}
          runs selected
        </div>
        <div className="text-slate-400" data-id="element-489">
          7-day timeline • Use arrows to scroll
        </div>
      </div>
    </motion.div>;
}